import React, { useMemo } from "react";
import { Box } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import MidasLogo from "../../../customer/midas/components/MidasLogo";
import { dt } from "../../themes/redesignTokens";
import { trimUnclosedListRefTail, parseListRefs } from "../streamBotMessage";
import ChatListRefChip from "./ChatListRefChip";

// Design tokens matching ChatBotMessage
const midas = {
  gold: "#D4A017",
  goldSoft: "rgba(212, 160, 23, 0.08)",
};

// Minimal markdown components matching ChatBotMessage styling
const markdownComponents: object = {
  p: ({ children }: any) => (
    <Box
      component="p"
      sx={{
        margin: 0,
        marginBottom: "8px",
        fontFamily: dt.font,
        fontSize: "14px",
        fontWeight: 400,
        color: dt.textPrimary,
        lineHeight: 1.6,
        "&:last-child": { marginBottom: 0 },
      }}
    >
      {children}
    </Box>
  ),
  strong: ({ children }: any) => (
    <Box component="strong" sx={{ fontWeight: 600 }}>
      {children}
    </Box>
  ),
  em: ({ children }: any) => (
    <Box component="em" sx={{ fontStyle: "italic" }}>
      {children}
    </Box>
  ),
  ul: ({ children }: any) => (
    <Box
      component="ul"
      sx={{
        fontFamily: dt.font,
        fontSize: "13px",
        pl: "16px",
        my: "4px",
        "& li::marker": { color: midas.gold },
      }}
    >
      {children}
    </Box>
  ),
  ol: ({ children }: any) => (
    <Box
      component="ol"
      sx={{ fontFamily: dt.font, fontSize: "13px", pl: "16px", my: "4px" }}
    >
      {children}
    </Box>
  ),
  li: ({ children }: any) => (
    <Box component="li" sx={{ my: "4px", lineHeight: 1.5 }}>
      {children}
    </Box>
  ),
  code: ({ inline, children, className }: any) => {
    if (inline) {
      return (
        <Box
          component="code"
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "12px",
            backgroundColor: "#F0F1F3",
            padding: "2px 6px",
            borderRadius: "4px",
          }}
        >
          {children}
        </Box>
      );
    }
    return (
      <Box
        component="pre"
        sx={{
          fontFamily: dt.fontMono,
          fontSize: "12px",
          backgroundColor: "#F8F9FA",
          border: `1px solid ${dt.cardBorder}`,
          borderRadius: "8px",
          padding: "10px 14px",
          overflowX: "auto",
          my: "8px",
        }}
      >
        <code className={className}>{children}</code>
      </Box>
    );
  },
};

/**
 * Sanitize streaming markdown to prevent layout jumps from incomplete syntax.
 * Closes unclosed bold/italic markers and removes dangling link syntax.
 */
const sanitizeStreamingMarkdown = (text: string): string => {
  if (!text) return text;

  let result = text;

  // Close unclosed bold markers (**...) — count occurrences
  const boldCount = (result.match(/\*\*/g) || []).length;
  if (boldCount % 2 !== 0) {
    // Odd number of ** means one is unclosed — render text as-is without the dangling **
    const lastBoldIdx = result.lastIndexOf("**");
    result = result.slice(0, lastBoldIdx) + result.slice(lastBoldIdx + 2);
  }

  // Close unclosed italic markers (*...) — but skip ** pairs
  const withoutBold = result.replace(/\*\*/g, "");
  const italicCount = (withoutBold.match(/\*/g) || []).length;
  if (italicCount % 2 !== 0) {
    const lastItalicIdx = result.lastIndexOf("*");
    result = result.slice(0, lastItalicIdx) + result.slice(lastItalicIdx + 1);
  }

  // Remove dangling link syntax: [text]( without closing )
  // Matches [...]( at the end of string without a closing paren
  result = result.replace(/\[[^\]]*\]\([^)]*$/, "");
  // Also remove lone [ at the very end (start of a link)
  result = result.replace(/\[[^\]]*$/, "");

  return result;
};

interface ChatStreamingMessageProps {
  text: string;
  showAvatar?: boolean;
}

const ChatStreamingMessage: React.FC<ChatStreamingMessageProps> = ({
  text,
  showAvatar = true,
}) => {
  // MC-25: Hold back any half-open <list-ref tag so we never flicker literal
  // markup into the DOM mid-stream. `safe` is render-safe; `tail` is buffered
  // and will arrive in the next render when more characters land.
  const { safePrefix, segments } = useMemo(() => {
    const { safe } = trimUnclosedListRefTail(text);
    const sanitized = sanitizeStreamingMarkdown(safe);
    return { safePrefix: sanitized, segments: parseListRefs(sanitized) };
  }, [text]);

  // Fast path: no <list-ref> tags in the safe prefix — single markdown block
  const fastPath = segments.length <= 1 && (segments.length === 0 || segments[0].kind === "text");

  return (
    <Box sx={{ position: "relative" }}>
      {/* Avatar — pulse animation while streaming */}
      {showAvatar && (
        <Box sx={{ position: "absolute", left: 0, top: 0 }}>
          <MidasLogo size="lg" animated="pulse" />
        </Box>
      )}

      {/* Message bubble */}
      <Box
        sx={{
          ml: "36px",
          maxWidth: "85%",
          backgroundColor: midas.goldSoft,
          border: "1px solid rgba(212, 160, 23, 0.12)",
          borderRadius: "4px 14px 14px 14px",
          padding: "12px 16px",
          animation: "midasBotMsg 0.25s cubic-bezier(0.4, 0, 0.2, 1) both",
        }}
      >
        {/* Markdown content — renders progressively as tokens arrive.
            MC-25: chips in the streaming state are not yet clickable
            (artifacts may still be arriving); they gain interactivity on
            the final message render. */}
        {fastPath ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {safePrefix}
          </ReactMarkdown>
        ) : (
          <Box sx={{ display: "inline" }}>
            {segments.map((seg, idx) => {
              if (seg.kind === "text") {
                return (
                  <Box key={idx} component="span" sx={{ "& > p": { display: "inline", margin: 0 } }}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={markdownComponents}
                    >
                      {seg.text}
                    </ReactMarkdown>
                  </Box>
                );
              }
              return (
                <ChatListRefChip
                  key={idx}
                  listId={seg.listId}
                  page={seg.page}
                  name={seg.name}
                  label={seg.label}
                  // Not clickable during streaming — final render wires the handler
                />
              );
            })}
          </Box>
        )}

        {/* Blinking cursor to indicate still streaming */}
        <Box
          component="span"
          sx={{
            display: "inline-block",
            width: "2px",
            height: "14px",
            backgroundColor: midas.gold,
            ml: "2px",
            verticalAlign: "text-bottom",
            animation: "midasCursorBlink 0.8s ease-in-out infinite",
            "@keyframes midasCursorBlink": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0 },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default ChatStreamingMessage;
